import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, TriangleAlert } from "lucide-react";
import { Fragment, useState } from "react";
import type { WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatDate } from "@/components/table/cells";
import { Input } from "@/components/ui/input";
import { TRANSFER_FIELDS } from "@/modules/warehouses/fields";
import { RejectionNotice } from "@/modules/warehouses/screens/RejectionNotice";
import type { TransferRow } from "@/modules/warehouses/projections";
import {
  buildReceiveInput,
  isReceiveDraftValid,
  remainingToReceive,
  requiresDiscrepancyReason,
} from "@/modules/warehouses/transferDrafts";
import { useVariantCatalog } from "@/modules/warehouses/variantCatalog";

// Reused from the list's own field vocabulary rather than a second copy of
// the labels — the same trick `ProductDetail.tsx` uses for `status` off
// `CATALOG_FIELDS`, so "Partially received" reads the same word here as it
// does in the table.
const STATUS_FIELD = TRANSFER_FIELDS.find((field) => field.name === "status");
const STATUS_LABEL = new Map(
  (STATUS_FIELD?.options ?? []).map((option) => [option.value, option.label]),
);

/** How each `TransferEvent.eventType` reads on screen (FR-WHS-9). */
const TRANSFER_EVENT_LABELS: Record<string, string> = {
  created: "Created",
  dispatched: "Dispatched",
  dispatch_failed: "Dispatch failed",
  line_dispatched: "Line dispatched",
  line_dispatch_compensated: "Line dispatch reversed",
  received: "Received",
  partially_received: "Partially received",
  line_received: "Line received",
  cancelled: "Cancelled",
  cancel_failed: "Cancel failed",
  line_cancel_returned: "Line returned to source",
  line_cancel_compensated: "Line return reversed",
};

function transferEventLabel(eventType: string): string {
  return TRANSFER_EVENT_LABELS[eventType] ?? eventType.replace(/_/g, " ");
}

/** Dispatch and cancel share the same "click, then confirm" shape. */
function ConfirmableAction({
  label,
  confirmLabel,
  warning,
  variant,
  pending,
  rejection,
  onConfirm,
  onDismiss,
}: {
  label: string;
  confirmLabel: string;
  warning: string;
  variant: "default" | "destructive";
  pending: boolean;
  rejection: WriteRejection | null;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <Button
        type="button"
        size="sm"
        variant={variant === "destructive" ? "outline" : "default"}
        onClick={() => {
          setConfirming(true);
        }}
      >
        {label}
      </Button>
    );
  }

  return (
    <div className="bg-muted/30 space-y-2 rounded-md border p-3">
      <p className="flex items-start gap-1.5 text-xs">
        <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        {warning}
      </p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={variant}
          disabled={pending}
          onClick={onConfirm}
        >
          {pending ? <Loader2 className="animate-spin" /> : null}
          {confirmLabel}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={pending}
          onClick={() => {
            setConfirming(false);
            onDismiss();
          }}
        >
          Back
        </Button>
      </div>
      {rejection ? (
        <RejectionNotice rejection={rejection} onReload={onDismiss} />
      ) : null}
    </div>
  );
}

/**
 * One transfer: its route, its lines, and whatever write it can still take.
 *
 * Rendered with `key={transfer.id}` by `TransfersList` — every piece of
 * local state below (a confirm step mid-click, a half-typed receipt
 * quantity, a rejection still on screen) belongs to one transfer, and
 * switching the selection should discard it rather than carry a Mumbai
 * receipt draft over onto a Jaipur transfer that happens to render next in
 * the same slot.
 */
export function TransferDetail({ transfer }: { transfer: TransferRow | null }) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const catalog = useVariantCatalog(transfer !== null);

  const [dispatchRejection, setDispatchRejection] =
    useState<WriteRejection | null>(null);
  const [cancelRejection, setCancelRejection] = useState<WriteRejection | null>(
    null,
  );
  const [receiveRejection, setReceiveRejection] =
    useState<WriteRejection | null>(null);
  const [receiveDrafts, setReceiveDrafts] = useState<Record<string, string>>(
    () =>
      Object.fromEntries(
        (transfer?.lines ?? []).map((line) => [
          line.id,
          String(remainingToReceive(line)),
        ]),
      ),
  );
  const [receiveReasons, setReceiveReasons] = useState<Record<string, string>>(
    () =>
      Object.fromEntries(
        (transfer?.lines ?? []).map((line) => [
          line.id,
          line.discrepancyReason ?? "",
        ]),
      ),
  );

  const historyQuery = useQuery({
    queryKey: ["warehouses", "transfers", transfer?.id, "history"],
    queryFn: () =>
      data.warehouses.transferHistory(transfer?.id ?? "", { limit: 25 }),
    enabled: transfer !== null,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["warehouses"] });

  const dispatchMutation = useMutation({
    mutationFn: (id: string) => data.warehouses.dispatchTransfer(id),
    onSuccess: (result) => {
      if (result.ok) {
        setDispatchRejection(null);
        void invalidate();
      } else {
        setDispatchRejection(result.rejection);
      }
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) =>
      data.warehouses.cancelTransfer(id, "Cancelled from the Transfers screen"),
    onSuccess: (result) => {
      if (result.ok) {
        setCancelRejection(null);
        void invalidate();
      } else {
        setCancelRejection(result.rejection);
      }
    },
  });

  const receiveMutation = useMutation({
    mutationFn: (id: string) =>
      data.warehouses.receiveTransfer(
        id,
        buildReceiveInput(transfer?.lines ?? [], receiveDrafts, receiveReasons),
      ),
    onSuccess: (result) => {
      if (result.ok) {
        setReceiveRejection(null);
        void invalidate();
      } else {
        setReceiveRejection(result.rejection);
      }
    },
  });

  if (!transfer) {
    return (
      <Card className="w-full shrink-0 lg:w-96">
        <CardContent className="text-muted-foreground text-sm">
          No transfer to show.
        </CardContent>
      </Card>
    );
  }

  const labelForVariant = (variantId: string): string => {
    const option = catalog.options.find(
      (candidate) => candidate.id === variantId,
    );
    if (!option) return variantId;
    return option.sku ? `${option.sku} — ${option.label}` : option.label;
  };

  return (
    <Card className="w-full shrink-0 lg:w-96">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {transfer.reference}
          <Badge variant="outline" className="font-normal">
            {STATUS_LABEL.get(transfer.status) ?? transfer.status}
          </Badge>
        </CardTitle>
        <CardDescription>
          {transfer.fromWarehouseName} → {transfer.toWarehouseName}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 text-sm">
        {transfer.note ? (
          <p className="text-muted-foreground">{transfer.note}</p>
        ) : null}

        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground text-left">
              <th className="pb-1 font-medium">Variant</th>
              <th className="pb-1 pl-2 text-right font-medium whitespace-nowrap">
                Dispatched
              </th>
              <th className="pb-1 pl-2 text-right font-medium whitespace-nowrap">
                Received
              </th>
              {transfer.status === "dispatched" ||
              transfer.status === "partially_received" ? (
                <th className="pb-1 pl-2 text-right font-medium whitespace-nowrap">
                  Receiving now
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {transfer.lines.map((line) => {
              const remaining = remainingToReceive(line);
              const receiving =
                transfer.status === "dispatched" ||
                transfer.status === "partially_received";
              const draftQuantity = Number(receiveDrafts[line.id] ?? "0");
              const needsReason =
                receiving &&
                requiresDiscrepancyReason(draftQuantity, remaining);
              return (
                <Fragment key={line.id}>
                  <tr className="border-t">
                    <td className="py-1.5 pr-2 break-words">
                      {labelForVariant(line.variantId)}
                    </td>
                    <td className="py-1.5 pl-2 text-right tabular-nums">
                      {line.quantityDispatched}
                    </td>
                    <td className="py-1.5 pl-2 text-right tabular-nums">
                      {line.quantityReceived}
                    </td>
                    {receiving ? (
                      <td className="py-1.5 pl-2 text-right">
                        {remaining === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <Input
                            type="number"
                            min={0}
                            max={remaining}
                            step={1}
                            value={receiveDrafts[line.id] ?? "0"}
                            onChange={(event) => {
                              setReceiveDrafts((current) => ({
                                ...current,
                                [line.id]: event.target.value,
                              }));
                            }}
                            className="ml-auto h-7 w-16 text-right"
                            aria-label={`Quantity received for ${labelForVariant(line.variantId)}`}
                          />
                        )}
                      </td>
                    ) : null}
                  </tr>
                  {needsReason ? (
                    <tr>
                      <td colSpan={4} className="pb-1.5 pl-2">
                        <Input
                          value={receiveReasons[line.id] ?? ""}
                          onChange={(event) => {
                            setReceiveReasons((current) => ({
                              ...current,
                              [line.id]: event.target.value,
                            }));
                          }}
                          placeholder="Why does this fall short of what remains? (required)"
                          className="h-7 text-xs"
                          aria-label={`Discrepancy reason for ${labelForVariant(line.variantId)}`}
                        />
                      </td>
                    </tr>
                  ) : !receiving && line.discrepancyReason ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="text-muted-foreground pb-1.5 pl-2"
                      >
                        {line.discrepancyReason}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {catalog.isError ? (
          <p className="text-destructive text-xs">
            Variant names could not be loaded; showing ids instead.
          </p>
        ) : null}

        <div className="space-y-3 border-t pt-3">
          {transfer.status === "draft" ? (
            <ConfirmableAction
              label="Dispatch"
              confirmLabel="Confirm dispatch"
              warning={`This decrements stock at ${transfer.fromWarehouseName} and cannot be undone once accepted.`}
              variant="default"
              pending={dispatchMutation.isPending}
              rejection={dispatchRejection}
              onConfirm={() => {
                dispatchMutation.mutate(transfer.id);
              }}
              onDismiss={() => {
                setDispatchRejection(null);
              }}
            />
          ) : null}

          {transfer.status === "dispatched" ||
          transfer.status === "partially_received" ? (
            <div className="space-y-1.5">
              <Button
                type="button"
                size="sm"
                disabled={
                  receiveMutation.isPending ||
                  !isReceiveDraftValid(
                    transfer.lines,
                    receiveDrafts,
                    receiveReasons,
                  )
                }
                onClick={() => {
                  receiveMutation.mutate(transfer.id);
                }}
              >
                {receiveMutation.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : null}
                Record receipt
              </Button>
              <p className="text-muted-foreground text-xs">
                Enter what actually arrived — less than dispatched leaves the
                rest in transit rather than closing the transfer short.
              </p>
              {receiveRejection ? (
                <RejectionNotice
                  rejection={receiveRejection}
                  onReload={() => {
                    setReceiveRejection(null);
                  }}
                />
              ) : null}
            </div>
          ) : null}

          {transfer.status !== "cancelled" ? (
            <ConfirmableAction
              label="Cancel transfer"
              confirmLabel="Confirm cancel"
              warning="This cancels the transfer. A received transfer cannot be cancelled."
              variant="destructive"
              pending={cancelMutation.isPending}
              rejection={cancelRejection}
              onConfirm={() => {
                cancelMutation.mutate(transfer.id);
              }}
              onDismiss={() => {
                setCancelRejection(null);
              }}
            />
          ) : null}
        </div>

        <div className="space-y-1.5 border-t pt-3">
          <p className="text-xs font-medium">History</p>
          {historyQuery.isPending ? (
            <p className="text-muted-foreground text-xs">Loading…</p>
          ) : historyQuery.isError ? (
            <p className="text-destructive text-xs">
              {historyQuery.error.message}
            </p>
          ) : historyQuery.data.items.length === 0 ? (
            <p className="text-muted-foreground text-xs">No history yet.</p>
          ) : (
            <ul className="space-y-1">
              {historyQuery.data.items.map((event) => (
                <li
                  key={event.id}
                  className="flex items-center justify-between gap-2 text-xs"
                >
                  <span>{transferEventLabel(event.eventType)}</span>
                  <span className="text-muted-foreground tabular-nums">
                    {formatDate(event.occurredAt, true)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
